/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON boundary */
import { supabase } from "@/integrations/supabase/client";

export type NoteFolder = {
  id: string;
  workspace_id: string;
  parent_folder_id: string | null;
  name: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type Note = {
  id: string;
  workspace_id: string;
  folder_id: string | null;
  title: string | null;
  body: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

const foldersTable = () => (supabase as any).from("composer_note_folders");
const notesTable = () => (supabase as any).from("composer_notes");

// ---- folders ----

export const listNoteFolders = async (workspaceId: string): Promise<NoteFolder[]> => {
  const { data, error } = await foldersTable()
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as NoteFolder[];
};

export const createNoteFolder = async (args: {
  workspaceId: string;
  name: string;
  parentFolderId?: string | null;
}): Promise<NoteFolder> => {
  const trimmed = args.name.trim();
  if (!trimmed) throw new Error("Folder name is required");
  const { data, error } = await foldersTable()
    .insert({
      workspace_id: args.workspaceId,
      name: trimmed,
      parent_folder_id: args.parentFolderId ?? null,
      sort_order: Date.now(),
    })
    .select("*")
    .single();
  if (error) throw error;
  return data as NoteFolder;
};

export const renameNoteFolder = async (id: string, name: string): Promise<void> => {
  const trimmed = name.trim();
  if (!trimmed) throw new Error("Folder name is required");
  const { error } = await foldersTable().update({ name: trimmed }).eq("id", id);
  if (error) throw error;
};

export const deleteNoteFolder = async (id: string): Promise<void> => {
  // Children cascade (parent FK ON DELETE CASCADE). Notes inside this folder are
  // un-filed via the notes FK ON DELETE SET NULL — content is preserved.
  const { error } = await foldersTable().delete().eq("id", id);
  if (error) throw error;
};

export const moveNoteFolder = async (args: {
  id: string;
  newParentId: string | null;
  sortOrder?: number;
}): Promise<void> => {
  const patch: any = { parent_folder_id: args.newParentId };
  if (typeof args.sortOrder === "number") patch.sort_order = args.sortOrder;
  const { error } = await foldersTable().update(patch).eq("id", args.id);
  if (error) throw error;
};

// ---- notes ----

export const listNotes = async (workspaceId: string): Promise<Note[]> => {
  const { data, error } = await notesTable()
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as Note[];
};

export const createNote = async (args: {
  workspaceId: string;
  folderId?: string | null;
  title?: string | null;
  body?: string;
}): Promise<Note> => {
  const { data: userData } = await supabase.auth.getUser();
  const { data, error } = await notesTable()
    .insert({
      workspace_id: args.workspaceId,
      folder_id: args.folderId ?? null,
      title: args.title?.trim() ? args.title.trim() : null,
      body: args.body ?? "",
      created_by: userData.user?.id ?? null,
      sort_order: Date.now(),
    })
    .select("*")
    .single();
  if (error) throw error;
  return data as Note;
};

export const updateNote = async (
  id: string,
  patch: { title?: string | null; body?: string },
): Promise<void> => {
  const update: any = {};
  if (patch.title !== undefined) {
    update.title = patch.title && patch.title.trim() ? patch.title.trim() : null;
  }
  if (patch.body !== undefined) update.body = patch.body;
  if (Object.keys(update).length === 0) return;
  const { error } = await notesTable().update(update).eq("id", id);
  if (error) throw error;
};

export const moveNote = async (id: string, folderId: string | null): Promise<void> => {
  const { error } = await notesTable().update({ folder_id: folderId }).eq("id", id);
  if (error) throw error;
};

export const reorderNote = async (id: string, newSortOrder: number): Promise<void> => {
  const { error } = await notesTable().update({ sort_order: newSortOrder }).eq("id", id);
  if (error) throw error;
};

export const deleteNote = async (id: string): Promise<void> => {
  const { error } = await notesTable().delete().eq("id", id);
  if (error) throw error;
};

// ---- helpers ----

export type NoteFolderNode = NoteFolder & { children: NoteFolderNode[]; noteCount: number };

export const buildNoteFolderTree = (
  folders: NoteFolder[],
  notes: Note[],
): NoteFolderNode[] => {
  const counts = new Map<string, number>();
  for (const n of notes) {
    if (!n.folder_id) continue;
    counts.set(n.folder_id, (counts.get(n.folder_id) ?? 0) + 1);
  }
  const byId = new Map<string, NoteFolderNode>();
  for (const f of folders) {
    byId.set(f.id, { ...f, children: [], noteCount: counts.get(f.id) ?? 0 });
  }
  const roots: NoteFolderNode[] = [];
  for (const f of folders) {
    const node = byId.get(f.id)!;
    if (f.parent_folder_id && byId.has(f.parent_folder_id)) {
      byId.get(f.parent_folder_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortRec = (nodes: NoteFolderNode[]) => {
    nodes.sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
};

export const noteDisplayTitle = (n: Note): string => {
  if (n.title && n.title.trim()) return n.title.trim();
  const firstLine = (n.body || "").split(/\r?\n/).find((l) => l.trim().length > 0);
  return firstLine?.trim().slice(0, 80) || "Untitled note";
};
